import { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, ChevronLeft, ChevronRight, Trash2, X } from "lucide-react";
import { toast } from "sonner";

// value 0 = "All" (backend returns everything when page_size <= 0)
const PAGE_SIZE_OPTIONS = [10, 50, 100, 300, 500, 1000, 0];
const sizeLabel = (n) => (n === 0 ? "All" : String(n));

export default function AdminAudit() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [mrId, setMrId] = useState("all");
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get("/admin/employees?page_size=500").then(({ data }) => setEmployees(data.data)).catch(() => {});
  }, []);

  const baseParams = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (mrId !== "all") params.set("user_id", mrId);
    return params;
  };

  const fetchLogs = () => {
    const params = baseParams();
    params.set("page", String(page));
    params.set("page_size", String(pageSize));
    api.get(`/admin/audit-logs?${params}`).then(({ data }) => {
      setLogs(data.data);
      setTotal(data.total);
      setSelected(new Set());
    });
  };

  useEffect(() => { fetchLogs(); }, [page, pageSize, dateFrom, dateTo, mrId]);

  const exportUrl = (fmt) => {
    const params = baseParams();
    params.set("fmt", fmt);
    return `${API}/admin/audit-logs?${params}`;
  };

  const onFromChange = (e) => { setDateFrom(e.target.value); setPage(1); };
  const onToChange = (e) => { setDateTo(e.target.value); setPage(1); };
  const onSizeChange = (v) => { setPageSize(Number(v)); setPage(1); };
  const onMrChange = (v) => { setMrId(v); setPage(1); };

  const totalPages = pageSize <= 0 ? 1 : Math.max(1, Math.ceil(total / pageSize));

  const mrOptions = employees.filter((e) => e.role === "mr");

  const allPageSelected = logs.length > 0 && logs.every((l) => selected.has(l.id));

  const toggleAll = () => {
    if (allPageSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        logs.forEach((l) => next.delete(l.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        logs.forEach((l) => next.add(l.id));
        return next;
      });
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} audit log ${selected.size === 1 ? "entry" : "entries"}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const { data } = await api.delete("/admin/audit-logs", { data: { ids: [...selected] } });
      toast.success(`Deleted ${data.deleted} ${data.deleted === 1 ? "entry" : "entries"}`);
      fetchLogs();
    } catch {
      toast.error("Failed to delete. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Shell role="admin">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Audit Log</div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Activity History</h1>
      </div>

      {/* Filters + Export */}
      <Card className="p-4 mb-4">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">MR</Label>
            <Select value={mrId} onValueChange={onMrChange}>
              <SelectTrigger className="h-10 w-full lg:w-52"><SelectValue placeholder="All MRs" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All MRs</SelectItem>
                {mrOptions.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name} ({e.emp_code})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">From</Label>
            <Input type="date" value={dateFrom} onChange={onFromChange} className="h-10 w-full lg:w-40" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">To</Label>
            <Input type="date" value={dateTo} onChange={onToChange} className="h-10 w-full lg:w-40" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Rows</Label>
            <Select value={String(pageSize)} onValueChange={onSizeChange}>
              <SelectTrigger className="h-10 w-full lg:w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <SelectItem key={n} value={String(n)}>{sizeLabel(n)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1" />
          <div className="flex gap-2 shrink-0">
            {["csv", "xlsx", "pdf"].map((f) => (
              <Button key={f} asChild size="sm" variant="outline" className="gap-1.5">
                <a href={exportUrl(f)} download>
                  <Download className="w-3.5 h-3.5" />{f.toUpperCase()}
                </a>
              </Button>
            ))}
          </div>
        </div>
        {mrId !== "all" && (
          <p className="text-xs text-slate-500 mt-2">Showing activity and exports for the selected MR only.</p>
        )}
      </Card>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-3 px-4 py-2.5 rounded-lg bg-brand-800 text-white text-sm">
          <span className="font-semibold">{selected.size} selected</span>
          <span className="text-slate-400 flex-1">{pageSize <= 0 ? "across all entries" : "on this page"}</span>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 gap-1.5 text-white hover:bg-slate-700 hover:text-white"
            onClick={clearSelection}
          >
            <X className="w-3.5 h-3.5" /> Clear
          </Button>
          <Button
            size="sm"
            className="h-8 gap-1.5 bg-red-600 hover:bg-red-700 text-white"
            onClick={deleteSelected}
            disabled={deleting}
          >
            <Trash2 className="w-3.5 h-3.5" />
            {deleting ? "Deleting..." : `Delete ${selected.size}`}
          </Button>
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 cursor-pointer"
                    checked={allPageSelected}
                    onChange={toggleAll}
                    title={allPageSelected ? "Deselect all shown" : "Select all shown"}
                  />
                </th>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Doctor</th>
                <th className="px-4 py-3">Language</th>
              </tr>
            </thead>
            <tbody data-testid="audit-table-body">
              {logs.map((l) => (
                <tr
                  key={l.id}
                  className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${selected.has(l.id) ? "bg-blue-50 hover:bg-blue-50" : ""}`}
                  onClick={() => toggleOne(l.id)}
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 cursor-pointer"
                      checked={selected.has(l.id)}
                      onChange={() => toggleOne(l.id)}
                    />
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(l.at).toLocaleString()}</td>
                  <td className="px-4 py-3"><Badge variant="secondary">{l.action}</Badge></td>
                  <td className="px-4 py-3">{l.user_name}<div className="text-xs text-slate-500">{l.user_emp_code}</div></td>
                  <td className="px-4 py-3">{l.doctor_name || "—"}</td>
                  <td className="px-4 py-3 uppercase text-slate-600">{l.language_code || "—"}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-slate-400">No activity</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/50">
          <div className="text-xs text-slate-500">
            {total} {total === 1 ? "entry" : "entries"}
            {pageSize > 0 && totalPages > 1 && <> · page {page} of {totalPages}</>}
            {selected.size > 0 && <> · <span className="text-blue-600 font-medium">{selected.size} selected</span></>}
          </div>
          {pageSize > 0 && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </Card>
    </Shell>
  );
}
